<script setup lang="ts">
/**
 * The chat dock.
 *
 * Sits in the bottom right corner over whatever screen the player is on, and
 * collapses to a single bar. Collapsed is the default: chat is something you
 * open, not something that takes a corner of the map away from you until you
 * close it.
 *
 * Everything arrives over the game socket, so there is no polling here and no
 * fetch on mount. A reconnect replays the buffer.
 */
import { computed, nextTick, ref, watch } from 'vue';

import { CHAT_LIMITS, ChatChannel, type ChatMessage } from '@waldermarch/shared';

import { play } from '@/audio';
import { useAuthStore } from '@/stores/auth';
import { useChatStore } from '@/stores/chat';

const chat = useChatStore();
const auth = useAuthStore();

const draft = ref('');
const log = ref<HTMLDivElement | null>(null);
const input = ref<HTMLInputElement | null>(null);

const tabs = computed(() => {
  const out: { id: ChatChannel; label: string }[] = [{ id: ChatChannel.WORLD, label: 'World' }];
  if (chat.hasAlliance) {
    out.push({ id: ChatChannel.ALLIANCE, label: chat.allianceName ?? 'Alliance' });
  }
  return out;
});

/**
 * Stick to the bottom, but only when the reader is already there. Yanking
 * someone back down while they are reading history is the classic chat bug.
 */
function atBottom(): boolean {
  const el = log.value;
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
}

async function scrollToBottom(): Promise<void> {
  await nextTick();
  const el = log.value;
  if (el) el.scrollTop = el.scrollHeight;
}

watch(
  () => chat.messages,
  async (next, previous) => {
    const grew = next.length > (previous?.length ?? 0);
    if (!chat.open) return;
    if (grew && !atBottom()) return;
    await scrollToBottom();
  },
);

async function toggle(): Promise<void> {
  chat.setOpen(!chat.open);
  play(chat.open ? 'clickSoft' : 'close');
  if (chat.open) {
    await scrollToBottom();
    input.value?.focus();
  }
}

async function pick(channel: ChatChannel): Promise<void> {
  if (channel === chat.active) return;
  chat.setActive(channel);
  play('clickSoft');
  await scrollToBottom();
}

async function submit(): Promise<void> {
  const text = draft.value.trim();
  if (!text) return;
  chat.post(text);
  draft.value = '';
  await scrollToBottom();
}

function when(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function isMine(message: ChatMessage): boolean {
  return message.authorId !== null && message.authorId === auth.user?.id;
}
</script>

<template>
  <div class="chat" :class="{ 'is-open': chat.open }">
    <button class="chat__bar" type="button" :aria-expanded="chat.open" @click="toggle">
      <span class="chat__title">Chat</span>
      <span
        v-if="!chat.open && chat.totalUnread > 0"
        class="chat__badge"
        :aria-label="`${chat.totalUnread} unread`"
      >
        {{ chat.totalUnread > 99 ? '99+' : chat.totalUnread }}
      </span>
      <span class="spacer"></span>
      <span class="chat__chevron" aria-hidden="true">{{ chat.open ? '▾' : '▴' }}</span>
    </button>

    <div v-show="chat.open" class="chat__body">
      <div class="chat__tabs" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="chat__tab"
          :class="{ 'is-active': chat.active === tab.id }"
          role="tab"
          :aria-selected="chat.active === tab.id"
          type="button"
          @click="pick(tab.id)"
        >
          {{ tab.label }}
          <span v-if="chat.unread[tab.id] > 0 && chat.active !== tab.id" class="chat__dot"></span>
        </button>
      </div>

      <div ref="log" class="chat__log">
        <p v-if="!chat.messages.length" class="chat__empty muted">
          {{
            chat.active === ChatChannel.ALLIANCE
              ? 'Nothing said in here yet.'
              : 'Nobody has said anything. Be the first.'
          }}
        </p>

        <p
          v-for="message in chat.messages"
          :key="message.id"
          class="chat__line"
          :class="{ 'is-system': message.system, 'is-mine': isMine(message) }"
        >
          <span class="chat__when">{{ when(message.at) }}</span>
          <template v-if="message.system">
            <em class="chat__body-text">{{ message.body }}</em>
          </template>
          <template v-else>
            <span class="chat__who">
              <span v-if="message.allianceTag" class="chat__tag">[{{ message.allianceTag }}]</span>
              {{ message.authorName }}
            </span>
            <span class="chat__body-text">{{ message.body }}</span>
          </template>
        </p>
      </div>

      <form class="chat__compose" @submit.prevent="submit">
        <input
          ref="input"
          v-model="draft"
          type="text"
          :maxlength="CHAT_LIMITS.BODY_MAX"
          :placeholder="
            chat.active === ChatChannel.ALLIANCE ? 'Message your alliance' : 'Message the world'
          "
          aria-label="Message"
          autocomplete="off"
        />
        <button class="btn btn--sm btn--primary" type="submit" :disabled="!draft.trim()">
          Send
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chat {
  position: fixed;
  right: 0.75rem;
  bottom: 0;
  z-index: 60;
  width: min(340px, calc(100vw - 1.5rem));
  display: flex;
  flex-direction: column;
  background: var(--stone-900);
  border: 1px solid var(--border-strong);
  border-bottom: none;
  border-radius: var(--radius) var(--radius) 0 0;
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.45);
  font-size: 0.82rem;
}

.chat__bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.4rem 0.6rem;
  background: none;
  border: none;
  border-radius: inherit;
  color: var(--parchment);
  cursor: pointer;

  &:hover { color: var(--gold); }
}

.chat__title {
  font-weight: 600;
  letter-spacing: 0.03em;
}

.chat__badge {
  min-width: 1.15rem;
  padding: 0 0.28rem;
  background: #8c3a2c;
  border-radius: 999px;
  color: #f4e6cf;
  font-size: 0.68rem;
  line-height: 1.15rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.chat__chevron {
  color: var(--parchment-faint);
  font-size: 0.7rem;
}

.chat__body {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border);
}

.chat__tabs {
  display: flex;
  gap: 0.2rem;
  padding: 0.35rem 0.4rem 0;
}

.chat__tab {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  background: none;
  border: 1px solid transparent;
  border-radius: var(--radius);
  color: var(--parchment-faint);
  font-size: 0.76rem;
  cursor: pointer;

  &:hover { color: var(--parchment); }

  &.is-active {
    border-color: var(--border-strong);
    background: var(--stone-950, rgba(0, 0, 0, 0.25));
    color: var(--gold);
  }
}

.chat__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8724f;
}

.chat__log {
  height: 15rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.4rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.chat__empty {
  margin: auto 0;
  text-align: center;
  font-size: 0.78rem;
}

.chat__line {
  margin: 0;
  line-height: 1.45;
  overflow-wrap: anywhere;

  &.is-system { color: var(--parchment-faint); }
  &.is-mine .chat__who { color: var(--gold); }
}

.chat__when {
  margin-right: 0.35rem;
  color: var(--parchment-faint);
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
}

.chat__who {
  margin-right: 0.3rem;
  color: #9fb8cc;
  font-weight: 600;
}

.chat__tag {
  color: var(--parchment-faint);
  font-weight: 400;
}

.chat__body-text { color: var(--parchment-dim); }

.chat__compose {
  display: flex;
  gap: 0.3rem;
  padding: 0.4rem;
  border-top: 1px solid var(--border);

  input {
    flex: 1;
    min-width: 0;
    padding: 0.25rem 0.45rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 700px) {
  .chat {
    right: 0.5rem;
    width: calc(100vw - 1rem);
  }

  .chat__log { height: 11rem; }
}
</style>
